import { SortCompareTypes } from '../../common/table/TableTypes';
import { DataTableRow } from '../../common/table/TableTypes';
import { DataTableSortableColumn } from '../../common/table/TableTypes';
import { SortOrder } from '../../common/types';
import { ActiveStatus, AutomationVersionHistoryData, SF_PLATFORM_DEPLOYED_BY_NAME } from './types';
import { DeployStatusForTable } from '../../../types/enums/DeployStatusForTable';
import { colors, Tag, Typography } from '@sweep-io/sweep-design';
import Box from '@mui/material/Box';
import { InfoTooltip } from '../../common/InfoTooltip';
import { Edit } from '@sweep-io/sweep-design/dist/icons';
import Stack from '@mui/material/Stack';
import { humanizeDate, humanizeDateVariants } from '../../helpers/humanizeDate';

export const getTableColumns = (
  historyDataList: AutomationVersionHistoryData[],
): DataTableSortableColumn[] => [
  {
    field: 'versionName',
    headerName: 'Version',
    width: '110px',
    showSortIcon: true,
    sortCompare: {
      type: SortCompareTypes.Custom,
      compareFunction: (a: DataTableRow, b: DataTableRow, order: SortOrder) => {
        const aData = historyDataList.find((item) => item.versionId === a.id);
        const bData = historyDataList.find((item) => item.versionId === b.id);
        // make sure the draft is always first
        if (!aData?.versionName && !bData?.versionName) return 0;
        if (!aData?.versionName) return -1;
        if (!bData?.versionName) return 1;
        return order === SortOrder.ASC
          ? aData.versionName.localeCompare(bData.versionName)
          : bData.versionName.localeCompare(aData.versionName);
      },
    },
  },
  {
    field: 'deployedByName',
    headerName: 'Deployed by',
    width: '320px',
    showSortIcon: true,
    sortCompare: {
      type: SortCompareTypes.Custom,
      compareFunction: (a: DataTableRow, b: DataTableRow, order: SortOrder) => {
        const aData = historyDataList.find((item) => item.versionId === a.id);
        const bData = historyDataList.find((item) => item.versionId === b.id);
        if (!aData?.deployedByName && !bData?.deployedByName) return 0;
        if (!aData?.deployedByName) return -1;
        if (!bData?.deployedByName) return 1;
        return order === SortOrder.ASC
          ? aData.deployedByName.localeCompare(bData.deployedByName)
          : bData.deployedByName.localeCompare(aData.deployedByName);
      },
    },
  },
  {
    field: 'deployedAt',
    headerName: 'Deployed At',
    width: '160px',
    showSortIcon: true,
    sortCompare: {
      type: SortCompareTypes.Custom,
      compareFunction: (a: DataTableRow, b: DataTableRow, order: SortOrder) => {
        const aData = historyDataList.find((item) => item.versionId === a.id);
        const bData = historyDataList.find((item) => item.versionId === b.id);
        if (!aData?.deployedAt && !bData?.deployedAt) return 0;
        if (!aData?.deployedAt) return -1;
        if (!bData?.deployedAt) return 1;
        const aDate = new Date(aData.deployedAt).getTime();
        const bDate = new Date(bData.deployedAt).getTime();
        return order === SortOrder.ASC ? aDate - bDate : bDate - aDate;
      },
    },
  },
  {
    field: 'status',
    headerName: 'Status',
    width: '120px',
  },
];

export const getRows = ({
  historyDataList,
  activeRow,
  usersById,
  versionToShowInfoOn,
}: {
  historyDataList: AutomationVersionHistoryData[];
  activeRow: any;
  usersById: any;
  versionToShowInfoOn: any;
}) => {
  return (
    historyDataList?.map((data) => {
      const isDraft = data.status === DeployStatusForTable.Draft;
      const isSFplatform = data?.deployedByName === SF_PLATFORM_DEPLOYED_BY_NAME;
      return {
        isRowActive: data.versionId === activeRow,
        id: data.versionId,
        versionName: (
          <>
            {!isDraft && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body-medium">{data.versionName}</Typography>
                  {data.isLive && (
                    <Tag label="Live" size="small" textTheme="light" color={colors.mint[700]} />
                  )}
                  {versionToShowInfoOn === data.versionId && (
                    <InfoTooltip
                      title={
                        'Version history is now available in the UI, starting from this version'
                      }
                    />
                  )}
                </Box>
              </>
            )}
            {isDraft && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  backgroundColor: colors.white,
                  justifyContent: 'center',
                  border: `1px solid ${colors.grey[900]}`,
                  width: '66px',
                  height: '20px',
                  borderRadius: '2px',
                }}
              >
                <Typography variant="caption-medium">Draft</Typography>
                <Edit variant="tiny" />
              </Box>
            )}
          </>
        ),
        deployedByName: isDraft ? (
          <Stack>
            <Typography variant="body">
              Updated by {usersById[data.updatedById]?.[0]?.name}
            </Typography>
            <Typography variant="body">
              Updated at{' '}
              {humanizeDate({
                dateOrTimestamp: data.updatedAt ?? '',
                variant: humanizeDateVariants.TWO_DIGITS_WITH_TIME,
              })}
            </Typography>
          </Stack>
        ) : (
          <>
            {data.deployedByName}
            {isSFplatform && (
              <InfoTooltip
                title={
                  'This version was not deployed to this org by Sweep. It may have been added via Change Sets or copied from another org'
                }
              />
            )}
          </>
        ),
        deployedAt:
          isDraft || isSFplatform
            ? '-'
            : humanizeDate({
                dateOrTimestamp: data.deployedAt ?? '',
                variant: humanizeDateVariants.TWO_DIGITS_WITH_TIME,
              }),

        status: data.isActive ? ActiveStatus.ACTIVE : ActiveStatus.INACTIVE,
      };
    }) ?? []
  );
};
